<template>
	<div>
		<header class="page-title">
			<h2>Country Editor - World Privacy Report</h2>
			<div class="ml-auto">
				<a :disabled="loadCounter < 2" @click="showModal(0)" class="btn btn-md btn-success"><i class="fas fa-plus"></i> Add New Country</a>
			</div>
		</header>
		<form class="list-filter flex box">
			<div class="three form-group">
				<label for="filter"><i class="fas fa-fw fa-globe"></i> Continent</label>
				<select id="filter_continent" name="filter_continent" v-model="query.filter_continent">
					<option :selected="continent.toLowerCase() === query.filter_continent" :value="continent.toLowerCase()" v-for="continent in continents">{{ continent }}</option>
				</select>
			</div>
			<div class="nine form-group">
				<label for="filter"><i class="fas fa-fw fa-filter"></i> Filter</label>
				<div class="input-group">
					<input class="input-small" id="filter" name="filter" placeholder="Filter in Country" type="text" v-model="query.filter">
					<button class="btn btn-primary">Filter</button>
				</div>
			</div>
		</form>
		<table>
			<thead>
				<tr>
					<th>Country</th>
					<th>Article</th>
					<th>Provider List</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="country in orderedCountries">
					<td>{{ country.name }}</td>
					<td>{{ country.article_title }}</td>
					<td>{{ country.providerlist_name }}</td>
					<td class="nowrap">
						<a :disabled="loadCounter < 2" @click="showModal(country.id)" class="btn btn-icon btn-primary" title="Edit"><i class="fas fa-pencil-alt"></i></a>
						<a :disabled="loadCounter < 2" @click="copyCountry(country.id)" class="btn btn-icon btn-primary" title="Copy"><i class="fas fa-clone"></i></a>
						<a @click="deleteCountry(country.id)" class="btn btn-icon btn-danger" title="Delete"><i class="fas fa-trash-alt"></i></a>
					</td>
				</tr>
			</tbody>
		</table>


		<modal @close="closeModal" v-show="isModalVisible">
			<template slot="header">
				<h4 class="modal-title" v-show="currentCountry.id === 0">Add New Country</h4>
				<h4 class="modal-title" v-show="currentCountry.id !== 0">Edit Country</h4>
			</template>
			<template slot="body">

				<div class="flex box">
					<div class="eight-lg">
						<div class="card">
							<header class="card-header">
								<h4 class="card-title">General</h4>
							</header>
							<div class="card-body">
								<div class="form-group">
									<label for="country">Country</label>
									<select :disabled="currentCountry.id !== 0" @change="changeCountry" id="country" v-model="currentCountry.name">
										<option disabled v-show="currentCountry.id === 0">Select a country</option>
										<option :disabled="country.saved" :id="country.country" v-for="country in countryList" v-if="currentCountry.id === 0">{{ country.country }}</option>
										<option :disabled="!country.saved" :id="country.country" :selected="currentCountry.id !== 0 && country.country === currentCountry.name" v-for="country in countryList" v-if="currentCountry.id !== 0">{{ country.country }}</option>
									</select>
									<p class="input-error" v-show="error_messages.country_id !== ''">{{error_messages.country_id }}</p>
								</div>
								<div class="form-group">
									<label for="article">Article</label>
									<vue-select :options="orderedAllArticles" @change="changeArticle" id="article2" label="title" v-model="currentCountry.article_object">
										<option disabled v-show="currentCountry.article_object.id === 0" value=0>Select an article</option>
										<option :id="article.id" :value="article.id" v-for="article in orderedAllArticles">{{ article.title }} ({{ article.id }})</option>
									</vue-select>
									<p class="input-error" v-show="error_messages.article_id !== ''">{{ error_messages.article_id }}</p>
								</div>
								<div class="form-group">
									<label for="providerlist">Provider List</label>
									<vue-select :disabled="currentCountry.article_id !== 0 && allArticles[currentCountry.article_id].provider_list_id !== null" :options="orderedAllProviderLists" @change="changeProviderList" id="providerlist2" label="name" v-model="currentCountry.providerlist_object">
										<option disabled v-show="currentCountry.id === 0 && currentCountry.providerlist_object.id === 0" value=null>Select a Provider List</option>
									</vue-select>
									<p class="input-error" v-show="error_messages.providerlist_id !== ''">{{ error_messages.providerlist_id }}</p>
								</div>
							</div>
						</div>
						<div class="card">
							<header class="card-header language-list">
								<h4 class="card-title">Summary</h4>
								<nav>
									<ul class="tabs" v-if="languages">
										<li :class="index === selectedLanguage ? 'active' : ''" @click="setLanguage(index)" v-for="(lang, index) in languages">
											<a href="#">
												<img :alt="lang.title" :src="lang.flagIconUrl" :title="lang.title">
												{{ lang.title }}
											</a>
										</li>
									</ul>
								</nav>
							</header>
							<div class="card-body">
								<textarea v-model="currentCountry.translated[languages[selectedLanguage].locale].summary"></textarea>
								<p class="input-error" v-show="error_messages.summary !== ''">{{ error_messages.summary }}</p>
							</div>
						</div>
					</div>
					<div class="four-lg">
						<div class="card">
							<header class="card-header">
								<h4 class="card-title">Quick Stats</h4>
							</header>
							<div class="card-body">
								<div class="form-group">
									<label for="qs-freedom-index">Freedom on the Internet Score &nbsp;<a href="https://freedomhouse.org/report/countries-net-freedom-2018" rel="noopener noreferrer nofollow" target="_blank"><i class="fas fa-external-link-square-alt"></i></a></label>
									<input @keyup="numbersOnly" id="qs-freedom-index" max="100" min="0" pattern="^[0–9]$" type="number" v-model="currentCountry.freedom_index">
									<p class="input-error" v-show="error_messages.numbersonly !== ''">{{ error_messages.numbersonly }}</p>
								</div>
								<div class="form-group">
									<label for="qs-news-sites">News Sites</label>
									<select id="qs-news-sites" v-model="currentCountry.blocked_news">
										<option value="false">Unblocked</option>
										<option value="true">Blocked</option>
									</select>
								</div>
								<div class="form-group">
									<label for="qs-social-media">Social Media</label>
									<select id="qs-social-media" v-model="currentCountry.blocked_social">
										<option value="false">Unblocked</option>
										<option value="true">Blocked</option>
									</select>
								</div>
							</div>
						</div>
						<div class="card">
							<header class="card-header">
								<h4 class="card-title">Map</h4>
							</header>
							<div class="card-body">
								<div class="form-group">
									<label for="map-preview">Map Preview</label>
									<img :src="mapUrl" alt="" id="map-preview">
								</div>
								<div class="form-group">
									<label for="map-range">Zoom Level</label>
									<input @change="changeCountry" id="map-range" max="10" min="0" step="1" type="range" v-model="currentCountry.zoom">
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template slot="footer">
				<a @click="closeModal" class="btn btn-md btn-primary modal-close"><i class="fas fa-times"></i> Cancel</a>
				<a @click="saveCountry" class="btn btn-md btn-success" v-show="currentCountry.id === 0"><i class="fas fa-save"></i> Add New Country</a>
				<a @click="saveCountry" class="btn btn-md btn-success" v-show="currentCountry.id !== 0"><i class="fas fa-save"></i> Save Changes</a>
			</template>
		</modal>
	</div>
</template>

<script>
	import modal from '../../Modal/DefaultModal.vue';
	import Errors from '../../../Utils/Errors.js';
	import VueSelect from 'vue-select';

	export default {
		props: {
			data: {
				type: Object
			},
		},
		components: {
			modal,
			"vue-select": VueSelect,
		},
		data() {
			return {

				id: this.data.id,

				modules: [
					'content', 'recommendedarticles'
				],

				languages: this.data.languages,
				selectedLanguage: 0,
				activeTab: 'content',
				selectedArticle: 0,
				translations: this.data.translations,

				countries: this.data.specificData.countries,
				countryList: this.data.specificData.countryList,
				continents: this.data.specificData.continents,

				allArticles: {},
				allProviderLists: {},

				isModalVisible: false,

				query: {
					filter_continent: this.getParameterByName('filter_continent') ? this.getParameterByName('filter_continent') : 'all',
					filter: this.getParameterByName('filter') ? this.getParameterByName('filter') : ''
				},

				errors: new Errors(),

				loadCounter: 0,
				mapUrl: '',
				summary: '',

				currentCountry: {
					id: 0,
					name: 'Select a country',
					continent_id: '',
					article_id: 0,
					article_object: {
						id: 0,
						title: ''
					},
					providerlist_id: 0,
					providerlist_object: {
						id: 0,
						name: ''
					},
					freedom_index: 0,
					blocked_news: false,
					blocked_social: false,
					zoom: 5
				},
				currentCountrySave: {},

				error_messages: {},

				validation: true,
			}
		},
		created() {
			let vm = this;
			vm.resetErrorMessages();
			this.setLanguage(0);
		},
		mounted() {
			this.loadAllArticles();
			this.loadProviderLists();
			this.setLanguage(0);
		},
		computed: {
			orderedCountries: function () {
				return _.orderBy(this.countries, ['name', 'continent'], ['asc', 'asc'])
			},
			orderedAllArticles: function () {
				return _.orderBy(this.allArticles, ['title'], ['asc'])
			},
			orderedAllProviderLists: function () {
				return _.orderBy(this.allProviderLists, ['name'], ['asc'])
			}
		},
		methods: {
			changeTab(name) {
				this.activeTab = name;
			},
			showModal: function (id) {
				this.loadCountry(id);
				this.isModalVisible = true;
			},
			closeModal: function () {
				this.isModalVisible = false;
				window.location.reload();
			},
			setLanguage: function (index) {
				let vm = this;
				this.selectedLanguage = index;

				//for countries
				if (typeof this.currentCountry.translated === 'undefined') {
					this.currentCountry.translated = {};
				}
				if (typeof this.currentCountry.translated[this.languages[this.selectedLanguage].locale] === 'undefined') {
					this.currentCountry.translated[this.languages[this.selectedLanguage].locale] = {};
					this.currentCountry.translated[this.languages[this.selectedLanguage].locale].summary = '';
				}
			},
			loadCountry: function (id) {

				this.resetErrorMessages();

				if (id === 0) {
					this.mapUrl = 'https://maps.googleapis.com/maps/api/staticmap?center=barcelona,spain&zoom=0&format=jpg&size=400x250&key=AIzaSyDMV6HuJwk6Z7EeO_y8W5NwA33KgfT5cH8',

						this.currentCountry = {
							id: 0,
							name: 'Select a country',
							continent_id: '',
							article_id: 0,
							article_object: {
								id: 0,
								title: ''
							},
							providerlist_id: 0,
							providerlist_object: {
								id: 0,
								name: ''
							},
							freedom_index: 0,
							blocked_news: false,
							blocked_social: false,
							zoom: 5
						};
				} else {
					this.currentCountry = this.countries[id];
					this.currentCountry.article_object = this.allArticles[this.currentCountry.article_id];
					this.currentCountry.providerlist_object = this.allProviderLists[this.currentCountry.providerlist_id];
					this.mapUrl = 'https://maps.googleapis.com/maps/api/staticmap?center=' + this.currentCountry.name + '&zoom=' + this.currentCountry.zoom + '&format=jpg&size=400x250&key=AIzaSyDMV6HuJwk6Z7EeO_y8W5NwA33KgfT5cH8';
					if (typeof this.currentCountry.article_object === 'undefined') {
						this.currentCountry.article_object = {
							id: 0,
							title: ''
						};
					}

				}
				this.setLanguage(this.selectedLanguage);
			},
			changeCountry: function () {
				this.mapUrl = 'https://maps.googleapis.com/maps/api/staticmap?center=' + this.currentCountry.name + '&zoom=' + this.currentCountry.zoom + '&format=jpg&size=400x250&key=AIzaSyDMV6HuJwk6Z7EeO_y8W5NwA33KgfT5cH8';
			},
			loadAllArticles: function () {
				let vm = this;

				axios.get('/api/' + vm.$selectedSite.slug + '/countrieshub/getallarticles',
					{},
				).then(function (response) {
					vm.loadCounter++;
					vm.allArticles = response.data.articles;
				}).catch(function (e) {
					vm.errors.record(e.response.data);
				});
			},
			changeArticle: function () {
				if (this.currentCountry.article_object === null) {
					return true;
				}
				this.currentCountry.article_id = this.currentCountry.article_object.id;
				if (typeof this.allArticles[this.currentCountry.article_id] === 'undefined') {
					return true;
				}
				if (this.allArticles[this.currentCountry.article_id].provider_list_id === null) {
					this.currentCountry.providerlist_id = 0;
					this.currentCountry.providerlist_object = {
						id: 0,
						name: ''
					};
				}
				if (this.allArticles[this.currentCountry.article_id].provider_list_id !== null) {
					this.currentCountry.providerlist_id = this.allArticles[this.currentCountry.article_id].provider_list_id;
					this.currentCountry.providerlist_object = this.allProviderLists[this.currentCountry.providerlist_id];
				}
				this.mapUrl = 'https://maps.googleapis.com/maps/api/staticmap?center=' + this.currentCountry.name + '&zoom=5&format=jpg&size=400x250&key=AIzaSyDMV6HuJwk6Z7EeO_y8W5NwA33KgfT5cH8';
			},
			changeProviderList: function () {
				if (this.currentCountry.providerlist_object === null) {
					return true;
				}
				this.currentCountry.providerlist_id = this.currentCountry.providerlist_object.id;
			},
			loadProviderLists: function () {
				let vm = this;

				axios.get('/api/' + vm.$selectedSite.slug + '/countrieshub/getallproviderlist',
					{},
				).then(function (response) {
					vm.loadCounter++;
					vm.allProviderLists = response.data.providerlists;
				}).catch(function (e) {
					vm.errors.record(e.response.data);
				});
			},
			saveCountry: function () {
				let vm = this;

				this.validator();

				if (this.validation === false) {
					return true;
				}

				this.currentCountry.continent_id = this.countryList[this.currentCountry.name].continent;
				this.currentCountry.country_code = this.countryList[this.currentCountry.name].Two_Letter_Country_Code;

				let apiUrl = '/api/' + vm.$selectedSite.slug + '/countrieshub/update/' + vm.currentCountry.id;
				if (vm.currentCountry.id === 0) {
					apiUrl = '/api/' + vm.$selectedSite.slug + '/countrieshub/store';
				}

				axios.post(apiUrl,
					{
						country: vm.currentCountry
					},
				).then(function (response) {
					vm.$toastr.success('Country saved successfully.');
					location.reload();

				}).catch(function (e) {
					vm.$toastr.error('Country saving failed.', 'Error');
					vm.errors.record(e.response.data);
				});
			},
			copyCountry: function (id) {
				this.showModal(id);
				this.currentCountry.id = 0;
				this.currentCountry.name = 'Select a country';
			},
			deleteCountry: function (id) {
				let vm = this;

				vm.$dialog.confirm('Are you sure you want to delete this country?', {
						loader: true
					})
					.then((dialog) => {
						axios.delete('/api/' + vm.$selectedSite.slug + '/countrieshub/' + id,
							{},
						).then(function (response) {
							vm.$toastr.success('Country deleted successfully.');
							location.reload();

						}).catch(function (e) {
							vm.$toastr.error('Country delete failed.', 'Error');
							vm.errors.record(e.response.data);
						});
					});
			},
			getParameterByName: function (name, url) {
				if (!url) url = window.location.href;
				name = name.replace(/[\[\]]/g, '\\$&');
				var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
					results = regex.exec(url);
				if (!results) return null;
				if (!results[2]) return '';
				return decodeURIComponent(results[2].replace(/\+/g, ' '));
			},
			resetErrorMessages: function () {
				this.error_messages = {
					country_id: '',
					article_id: '',
					providerlist_id: '',
					summary: '',
					numbersonly: '',
				};
			},
			numbersOnly: function () {
				this.error_messages.numbersonly = '';
				var regex = /[0-9]|\./;
				if (!regex.test(this.currentCountry.freedom_index)) {
					this.error_messages.numbersonly = 'only number allowed here';
					this.validation = false;
				}
			},
			validator: function () {
				this.resetErrorMessages();
				this.validation = true;
				var summaryValidation = 0;

				if (this.currentCountry.name === 'Select a country') {
					this.error_messages.country_id = 'please select one';
					this.validation = false;
				}

				if (this.currentCountry.providerlist_object === null || this.currentCountry.providerlist_object.id === 0) {
					this.error_messages.providerlist_id = 'please select one';
					this.validation = false;
				}

				$.each(this.currentCountry.translated, function (key, langBlock) {
					if (langBlock.summary !== '') {
						summaryValidation++
					}
				});
				if (summaryValidation === 0) {
					this.error_messages.summary = 'please fill in';
					this.validation = false;
				}
				var regex = /[0-9]|\./;
				if (!regex.test(this.currentCountry.freedom_index)) {
					this.error_messages.numbersonly = 'only number allowed here';
					this.validation = false;
				}
				return this.validation;
			}

		}
	}
</script>

